/**
 * SidebarFilters Komponenti
 * 
 * Sidebar'da dinamik filtreleri gösterir
 * Admin panelinden `is_filterable = true` olan field'ları dinamik olarak gösterir
 */

'use client'

import { useMemo, useCallback, useState } from 'react'
import { motion } from 'framer-motion'
import FilterGroup from './FilterGroup'
import RangeFilter from './RangeFilter'
import YearRangeFilter from './YearRangeFilter'
import NumberRangeFilter from './NumberRangeFilter'
import DestinationAutocomplete from './DestinationAutocomplete'
import ListBox from '@/components/ui/ListBox'
import ThemeCheckbox from '@/components/ui/ThemeCheckbox'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'
import type { SidebarFilter, Suggestion, FilterConfig, CategoryTreeNode } from '@/types/product-search'

export interface SidebarFiltersProps {
    className?: string
    filters: FilterConfig | null
    activeFilters: Record<string, any>
    updateFilter: (fieldKey: string, value: any) => void
    getSuggestions: (params: Omit<{ product_type: string; field_key: string; query: string; source?: string; limit?: number }, 'product_type'>) => Promise<Suggestion[]>
    isFiltersLoading: boolean
    clearSidebarFilters?: () => void // Sadece sidebar filtrelerini temizle (MainSearchBar etkilenmez)
    /** Bu slug’a sahip kategori(ler) sidebar’da gizlenir (örn. group-tours sayfasında "Group Tours" kutusu) */
    hideCategorySlugs?: string[]
    /** Ana kategori sayfasındayken (üst kategori yok) kategori filtre kartının tamamı gizlenir */
    hideEntireCategoryFilterWhenMainCategory?: boolean
    /** Location (Filter By Location) grubunu sidebar'da gösterme */
    hideLocationGroup?: boolean
    /** Bu field_key'ler sidebar'da hiç gösterilmez (örn. tur listesinde tour_type) */
    hideFieldKeys?: string[]
}

export default function SidebarFilters({
    className = '',
    filters,
    activeFilters,
    updateFilter,
    getSuggestions,
    isFiltersLoading,
    clearSidebarFilters,
    hideCategorySlugs,
    hideEntireCategoryFilterWhenMainCategory = false,
    hideLocationGroup = false,
    hideFieldKeys = [],
}: SidebarFiltersProps) {
    const { getVariable } = useLanguageVariables()
    
    // Sidebar filtrelerini belirle (MainSearchBar'daki alanları hariç tut)
    const sidebarFilterKeys = useMemo(() => {
        if (!filters?.sidebar_filters) return []
        return filters.sidebar_filters.map(f => f.field_key)
    }, [filters])
    
    // Aktif sidebar filtrelerini kontrol et
    const hasActiveSidebarFilters = useMemo(() => {
        return sidebarFilterKeys.some(key => {
            const value = activeFilters[key]
            return value !== null && value !== undefined && value !== '' && 
                   (Array.isArray(value) ? value.length > 0 : true)
        })
    }, [activeFilters, sidebarFilterKeys])

    // Filtreleri gruplara göre organize et
    const groupedFilters = useMemo(() => {
        if (!filters?.sidebar_filters) {
            return {}
        }

        const groups: Record<string, SidebarFilter[]> = {}
        
        filters.sidebar_filters.forEach((filter) => {
            if (hideFieldKeys.length > 0 && hideFieldKeys.includes(filter.field_key)) {
                return
            }
            // Ana kategori sayfasındayken kategori filtre kartının tamamını gösterme
            if (hideEntireCategoryFilterWhenMainCategory && filter.field_type === 'category_tree') {
                return
            }
            // Geçersiz filtreleri atla (min/max yoksa range filtreleri, options yoksa checkbox/dropdown)
            if (filter.field_type === 'range' && (filter.min === undefined || filter.max === undefined)) {
                return
            }
            if ((filter.field_type === 'checkbox' || filter.field_type === 'dropdown') && (!filter.options || filter.options.length === 0)) {
                return
            }
            if (filter.field_type === 'category_tree' && (!filter.categories || filter.categories.length === 0)) {
                return
            }
            
            const groupKey = filter.group || 'other'
            if (!groups[groupKey]) {
                groups[groupKey] = []
            }
            groups[groupKey].push(filter)
        })

        // Boş grupları temizle
        Object.keys(groups).forEach(key => {
            if (groups[key].length === 0) {
                delete groups[key]
            }
        })

        return groups
    }, [filters, hideEntireCategoryFilterWhenMainCategory, hideFieldKeys])

    // Autocomplete search handler
    const handleAutocompleteSearch = useCallback(
        (fieldKey: string, autocompleteSource?: string) => async (query: string): Promise<Suggestion[]> => {
            // Minimum 2 karakter gerekli (DestinationAutocomplete'te 3 karakter kontrolü var ama burada early return için 2 yeterli)
            if (query.length < 2) return []
            return await getSuggestions({
                field_key: fieldKey,
                query,
                source: autocompleteSource,
                limit: 10,
            })
        },
        [getSuggestions]
    )

    // Autocomplete select handler (null = seçim temizlendi)
    const handleAutocompleteSelect = useCallback(
        (fieldKey: string) => (suggestion: Suggestion | null) => {
            if (!suggestion) {
                updateFilter(fieldKey, '')
                return
            }
            updateFilter(fieldKey, suggestion.slug || suggestion.name)
        },
        [updateFilter]
    )

    // Range filter change handler
    const handleRangeChange = useCallback(
        (fieldKey: string) => (value: { min: number; max: number }) => {
            updateFilter(fieldKey, value)
        },
        [updateFilter]
    )

    // Checkbox change handler
    const handleCheckboxChange = useCallback(
        (fieldKey: string, optionKey: string) => (checked: boolean) => {
            const currentValue = activeFilters[fieldKey] || []
            const valueArray = Array.isArray(currentValue) ? currentValue : [currentValue].filter(Boolean)
            
            if (checked) {
                if (!valueArray.includes(optionKey)) {
                    updateFilter(fieldKey, [...valueArray, optionKey])
                }
            } else {
                updateFilter(fieldKey, valueArray.filter((v) => v !== optionKey))
            }
        },
        [activeFilters, updateFilter]
    )

    // Dropdown change handler
    const handleDropdownChange = useCallback(
        (fieldKey: string) => (value: string) => {
            updateFilter(fieldKey, value)
        },
        [updateFilter]
    )

    // Category tree checkbox: category_ids array
    const handleCategoryTreeChange = useCallback(
        (fieldKey: string, categoryId: number) => (checked: boolean) => {
            const current = activeFilters[fieldKey]
            const ids: number[] = Array.isArray(current) ? current : (current ? [Number(current)] : [])
            if (checked) {
                if (!ids.includes(categoryId)) updateFilter(fieldKey, [...ids, categoryId])
            } else {
                updateFilter(fieldKey, ids.filter((id) => id !== categoryId))
            }
        },
        [activeFilters, updateFilter]
    )

    // Equipment search state - her filter için ayrı state
    const [equipmentSearchStates, setEquipmentSearchStates] = useState<Record<string, string>>({})
    
    const updateEquipmentSearch = useCallback((fieldKey: string, value: string) => {
        setEquipmentSearchStates(prev => ({ ...prev, [fieldKey]: value }))
    }, [])

    // Rental type seçeneklerini gruplandır ve sırala helper
    const groupRentalTypeOptions = useCallback((options: SidebarFilter['options'] = []) => {
        const rentalDurationOptions: typeof options = []
        const otherOptions: typeof options = []
        
        options.forEach(option => {
            const keyLower = option.key.toLowerCase()
            const labelLower = option.label.toLowerCase()
            
            // Daily ve Weekly'yi kiralama süresi grubuna al
            if (keyLower === 'daily' || keyLower === 'weekly' || 
                labelLower.includes('daily') || labelLower.includes('weekly') ||
                labelLower.includes('günlük') || labelLower.includes('haftalık')) {
                rentalDurationOptions.push(option)
            } else {
                otherOptions.push(option)
            }
        })
        
        // Kiralama süresi seçeneklerini sırala: Daily önce, Weekly sonra
        rentalDurationOptions.sort((a, b) => {
            const aKey = a.key.toLowerCase()
            const bKey = b.key.toLowerCase()
            const aLabel = a.label.toLowerCase()
            const bLabel = b.label.toLowerCase()
            
            // Daily her zaman önce
            if ((aKey === 'daily' || aLabel.includes('daily') || aLabel.includes('günlük')) &&
                (bKey !== 'daily' && !bLabel.includes('daily') && !bLabel.includes('günlük'))) {
                return -1
            }
            if ((bKey === 'daily' || bLabel.includes('daily') || bLabel.includes('günlük')) &&
                (aKey !== 'daily' && !aLabel.includes('daily') && !aLabel.includes('günlük'))) {
                return 1
            }
            
            // Weekly ikinci
            if ((aKey === 'weekly' || aLabel.includes('weekly') || aLabel.includes('haftalık')) &&
                (bKey !== 'weekly' && !bLabel.includes('weekly') && !bLabel.includes('haftalık'))) {
                return -1
            }
            if ((bKey === 'weekly' || bLabel.includes('weekly') || bLabel.includes('haftalık')) &&
                (aKey !== 'weekly' && !aLabel.includes('weekly') && !aLabel.includes('haftalık'))) {
                return 1
            }
            
            return 0
        })
        
        // Diğer seçenekleri alfabetik sırala (With Captain gibi önemli olanlar önce)
        otherOptions.sort((a, b) => {
            const aLabel = a.label.toLowerCase()
            const bLabel = b.label.toLowerCase()
            
            // "With Captain" veya "Captain" içerenler önce
            if (aLabel.includes('captain') && !bLabel.includes('captain')) return -1
            if (bLabel.includes('captain') && !aLabel.includes('captain')) return 1
            
            // Alfabetik sıralama
            return aLabel.localeCompare(bLabel)
        })
        
        return { rentalDurationOptions, otherOptions }
    }, [])

    // Field render helper
    const renderFilter = useCallback(
        (filter: SidebarFilter) => {
            const fieldValue = activeFilters[filter.field_key]

            switch (filter.field_type) {
                case 'range':
                    if (filter.min === undefined || filter.max === undefined) return null
                    
                    const rangeValue = fieldValue
                        ? typeof fieldValue === 'object' && 'min' in fieldValue && 'max' in fieldValue
                            ? { min: fieldValue.min, max: fieldValue.max }
                            : { min: filter.min, max: filter.max }
                        : { min: filter.min, max: filter.max }

                    // Range filter için label belirle
                    const rangeLabel = filter.label || getVariable(`filter_${filter.field_key}_label`, filter.field_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
                    
                    // Year için özel component (built_refit_year)
                    if (filter.field_key.includes('year') || filter.field_key === 'built_refit_year') {
                        return (
                            <div key={filter.field_key} className="space-y-3">
                                <YearRangeFilter
                                    fieldKey={filter.field_key}
                                    label={rangeLabel}
                                    min={filter.min}
                                    max={filter.max}
                                    value={rangeValue}
                                    onChange={handleRangeChange(filter.field_key)}
                                />
                            </div>
                        )
                    }
                    
                    // Capacity alanları için number input (cabin_count, guest_capacity, bathroom_wc, passenger_capacity)
                    const capacityFields = ['cabin_count', 'guest_capacity', 'bathroom_wc', 'passenger_capacity', 'wc']
                    if (capacityFields.some(field => filter.field_key.includes(field))) {
                        // Unit belirleme
                        let unit: string | undefined
                        if (filter.field_key.includes('cabin')) {
                            unit = getVariable('filter_unit_cabin', 'cabin')
                        } else if (filter.field_key.includes('guest') || filter.field_key.includes('passenger') || filter.field_key.includes('capacity')) {
                            unit = getVariable('filter_unit_guest', 'guest')
                        } else if (filter.field_key.includes('wc') || filter.field_key.includes('bathroom')) {
                            unit = getVariable('filter_unit_wc', 'WC')
                        }
                        
                        return (
                            <div key={filter.field_key} className="space-y-3">
                                <NumberRangeFilter
                                    fieldKey={filter.field_key}
                                    label={rangeLabel}
                                    min={filter.min}
                                    max={filter.max}
                                    step={filter.step || 1}
                                    value={rangeValue}
                                    onChange={handleRangeChange(filter.field_key)}
                                    unit={unit}
                                />
                            </div>
                        )
                    }
                    
                    // Diğer range filter'lar için normal RangeFilter (price, duration gibi)
                    const priceUnit = filter.field_key.includes('price')
                        ? (filters?.default_currency_symbol ?? undefined)
                        : undefined
                    const durationUnit = filter.field_key.includes('duration') ? 'day' : undefined
                    return (
                        <div key={filter.field_key} className="space-y-3">
                            <RangeFilter
                                fieldKey={filter.field_key}
                                label={rangeLabel}
                                min={filter.min}
                                max={filter.max}
                                step={filter.step || 1}
                                value={rangeValue}
                                onChange={handleRangeChange(filter.field_key)}
                                unit={priceUnit ?? durationUnit}
                            />
                        </div>
                    )

                case 'checkbox':
                    if (!filter.options || filter.options.length === 0) return null

                    // Ekipmanlar için özel işlem (field_key'e göre kontrol)
                    const isEquipmentFilter = filter.field_key?.toLowerCase().includes('equipment') || 
                                            filter.field_key?.toLowerCase().includes('ekipman') ||
                                            filter.group?.toLowerCase().includes('equipment') ||
                                            filter.group?.toLowerCase().includes('ekipman')
                    
                    // Rental type için gruplandırma
                    const isRentalTypeFilter = filter.field_key?.toLowerCase().includes('rental_type') || 
                                              filter.field_key?.toLowerCase().includes('rent_type')
                    
                    // Rental type seçeneklerini gruplandır
                    const groupedRentalOptions = isRentalTypeFilter ? groupRentalTypeOptions(filter.options) : null

                    // Ekipmanlar için search state
                    const equipmentSearch = equipmentSearchStates[filter.field_key] || ''
                    
                    // Filtrelenmiş seçenekler
                    const filteredOptions = isEquipmentFilter && equipmentSearch
                        ? filter.options.filter(option => {
                            const searchLower = equipmentSearch.toLowerCase()
                            return option.label.toLowerCase().includes(searchLower) ||
                                   option.key.toLowerCase().includes(searchLower)
                          })
                        : filter.options

                    return (
                        <div key={filter.field_key} className="space-y-3">
                            {/* Ekipmanlar için search input */}
                            {isEquipmentFilter && (
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={equipmentSearch}
                                        onChange={(e) => updateEquipmentSearch(filter.field_key, e.target.value)}
                                        placeholder={getVariable('filters_search_equipment', 'Search equipment...')}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-alsero-first focus:border-alsero-first transition-colors"
                                    />
                                    {equipmentSearch && (
                                        <button type="button"
                                            onClick={() => updateEquipmentSearch(filter.field_key, '')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            aria-label="Clear search"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            )}
                            
                            {/* Scroll container - sadece ekipmanlar için */}
                            <div className={isEquipmentFilter ? "max-h-[300px] overflow-y-auto pr-2 space-y-2.5" : "space-y-2.5"}>
                                {/* Rental type için gruplandırılmış gösterim */}
                                {isRentalTypeFilter && groupedRentalOptions ? (
                                    <>
                                        {/* Kiralama Süresi Grubu */}
                                        {groupedRentalOptions.rentalDurationOptions.length > 0 && (
                                            <div className="space-y-2.5 mb-4">
                                                <div className="text-xs font-semibold text-alsero-dark-gray uppercase tracking-wide mb-2">
                                                    {getVariable('filters_rental_duration', 'Rental Duration')}
                                                </div>
                                                {groupedRentalOptions.rentalDurationOptions.map((option) => {
                                                    const isChecked = Array.isArray(fieldValue)
                                                        ? fieldValue.includes(option.key)
                                                        : fieldValue === option.key

                                                    return (
                                                        <label
                                                            key={option.key}
                                                            className="flex items-center gap-3 cursor-pointer group"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(e) => handleCheckboxChange(filter.field_key, option.key)(e.target.checked)}
                                                                className="w-4 h-4 text-alsero-first border-2 border-gray-300 rounded focus:ring-2 focus:ring-alsero-first focus:ring-offset-0 cursor-pointer transition-colors checked:!bg-alsero-first checked:!border-alsero-first"
                                                                style={{
                                                                    accentColor: isChecked ? 'rgb(227, 30, 36)' : undefined,
                                                                }}
                                                            />
                                                            <span className="text-sm text-alsero-dark-gray group-hover:text-alsero-first transition-colors">
                                                                {option.label}
                                                            </span>
                                                        </label>
                                                    )
                                                })}
                                            </div>
                                        )}
                                        
                                        {/* Diğer Seçenekler Grubu */}
                                        {groupedRentalOptions.otherOptions.length > 0 && (
                                            <div className="space-y-2.5">
                                                {groupedRentalOptions.otherOptions.length > 0 && groupedRentalOptions.rentalDurationOptions.length > 0 && (
                                                    <div className="text-xs font-semibold text-alsero-dark-gray uppercase tracking-wide mb-2">
                                                        {getVariable('filters_other_options', 'Other Options')}
                                                    </div>
                                                )}
                                                {groupedRentalOptions.otherOptions.map((option) => {
                                                    const isChecked = Array.isArray(fieldValue)
                                                        ? fieldValue.includes(option.key)
                                                        : fieldValue === option.key

                                                    return (
                                                        <label
                                                            key={option.key}
                                                            className="flex items-center gap-3 cursor-pointer group"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(e) => handleCheckboxChange(filter.field_key, option.key)(e.target.checked)}
                                                                className="w-4 h-4 text-alsero-first border-2 border-gray-300 rounded focus:ring-2 focus:ring-alsero-first focus:ring-offset-0 cursor-pointer transition-colors checked:!bg-alsero-first checked:!border-alsero-first"
                                                                style={{
                                                                    accentColor: isChecked ? 'rgb(227, 30, 36)' : undefined,
                                                                }}
                                                            />
                                                            <span className="text-sm text-alsero-dark-gray group-hover:text-alsero-first transition-colors">
                                                                {option.label}
                                                            </span>
                                                        </label>
                                                    )
                                                })}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    // Normal checkbox listesi
                                    filteredOptions.map((option) => {
                                        const isChecked = Array.isArray(fieldValue)
                                            ? fieldValue.includes(option.key)
                                            : fieldValue === option.key

                                        return (
                                            <ThemeCheckbox
                                                key={option.key}
                                                id={`filter-${filter.field_key}-${option.key}`}
                                                checked={isChecked}
                                                onChange={handleCheckboxChange(filter.field_key, option.key)}
                                                label={option.label}
                                                labelClassName="group"
                                            />
                                        )
                                    })
                                )}
                            </div>
                            
                            {/* Ekipmanlar için sonuç sayısı */}
                            {isEquipmentFilter && equipmentSearch && (
                                <div className="text-xs text-alsero-light-gray pt-1">
                                    {getVariable('filters_showing_results', 'Showing')} {filteredOptions.length} {getVariable('filters_of', 'of')} {filter.options.length} {getVariable('filters_items', 'items')}
                                </div>
                            )}
                        </div>
                    )

                case 'autocomplete':
                    return (
                        <div key={filter.field_key} className="space-y-2">
                      
                            <DestinationAutocomplete
                                fieldKey={filter.field_key}
                                label="" // Label'ı DestinationAutocomplete içinde gösterme, üstte gösteriyoruz
                                placeholder={getVariable(`filter_${filter.field_key}_placeholder`, getVariable('filter_autocomplete_placeholder', `Search ${filter.label?.toLowerCase() || ''}...`).replace('{label}', filter.label || ''))}
                                value={typeof fieldValue === 'string' ? fieldValue : ''}
                                onChange={(value) => updateFilter(filter.field_key, value)}
                                onSelect={handleAutocompleteSelect(filter.field_key)}
                                getSuggestions={handleAutocompleteSearch(filter.field_key, filter.autocomplete_source)}
                                autoOpenDropdown={false} // SidebarFilters için dropdown otomatik açılmasın
                            />
                        </div>
                    )

                case 'category_tree':
                    if (!filter.categories || filter.categories.length === 0) return null
                    const selectedIds: number[] = Array.isArray(fieldValue) ? fieldValue : (fieldValue ? [Number(fieldValue)] : [])
                    const hideSlugs = hideCategorySlugs ?? []
                    const filterOutHiddenCategories = (nodes: CategoryTreeNode[]): CategoryTreeNode[] =>
                        nodes
                            .filter((n) => !hideSlugs.includes(n.slug))
                            .map((n) => ({
                                ...n,
                                children: n.children?.length ? filterOutHiddenCategories(n.children) : [],
                            }))
                    const categoriesToShow = hideSlugs.length > 0 ? filterOutHiddenCategories(filter.categories) : filter.categories
                    if (categoriesToShow.length === 0) return null
                    const renderCategoryLevel = (nodes: CategoryTreeNode[], depth: number) => (
                        <div key={nodes.map(n => n.id).join('-')} className={depth > 0 ? 'ml-3 mt-1 border-l border-gray-200 pl-2' : 'space-y-2'}>
                            {nodes.map((node) => {
                                const isChecked = selectedIds.includes(node.id)
                                return (
                                    <div key={node.id}>
                                        <ThemeCheckbox
                                            id={`category-filter-${filter.field_key}-${node.id}`}
                                            checked={isChecked}
                                            onChange={handleCategoryTreeChange(filter.field_key, node.id)}
                                            label={node.name}
                                            labelClassName="group"
                                        />
                                        {node.children && node.children.length > 0 && renderCategoryLevel(node.children, depth + 1)}
                                    </div>
                                )
                            })}
                        </div>
                    )
                    return (
                        <div
                            key={filter.field_key}
                            className="space-y-2 max-h-[280px] overflow-y-auto overflow-x-hidden pr-2 mega-menu-scroll"
                        >
                            {renderCategoryLevel(categoriesToShow, 0)}
                        </div>
                    )

                case 'dropdown':
                    if (!filter.options || filter.options.length === 0) return null

                    return (
                        <div key={filter.field_key} className="space-y-2">
                            <ListBox
                                title="" // Label'ı ListBox içinde gösterme, üstte gösteriyoruz
                                icon={null}
                                value={typeof fieldValue === 'string' ? fieldValue : ''}
                                onChange={handleDropdownChange(filter.field_key)}
                                options={filter.options.map((opt, index) => ({
                                    title: opt.label,
                                    value: opt.key,
                                    id: parseInt(opt.key) || index,
                                    icon: null,
                                }))}
                            />
                        </div>
                    )

                case 'date':
                    // Date picker - basit HTML5 date input
                    // İleride daha gelişmiş bir date picker komponenti eklenebilir
                    return (
                        <div key={filter.field_key} className="space-y-2">
          
                            <input
                                type="date"
                                value={typeof fieldValue === 'string' ? fieldValue : ''}
                                onChange={(e) => updateFilter(filter.field_key, e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-alsero-dark-gray bg-white focus:outline-none focus:ring-2 focus:ring-alsero-first focus:border-alsero-first transition-colors"
                            />
                        </div>
                    )

                default:
                    return null
            }
        },
        [activeFilters, updateFilter, handleAutocompleteSearch, handleAutocompleteSelect, handleRangeChange, handleCheckboxChange, handleDropdownChange, handleCategoryTreeChange, getVariable, equipmentSearchStates, updateEquipmentSearch, groupRentalTypeOptions, hideCategorySlugs]
    )

    // Loading state
    if (isFiltersLoading) {
        return (
            <div className={`space-y-0 ${className}`}>
                <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-32 bg-white border border-[#E7EBF3] rounded-lg p-6" />
                    ))}
                </div>
            </div>
        )
    }

    // Empty state - filtreler yoksa
    if (!filters?.sidebar_filters || filters.sidebar_filters.length === 0) {
        return (
            <div className={`space-y-0 ${className}`}>
                <div className="p-6 bg-white border border-[#E7EBF3] rounded-lg">
                    <div className="flex flex-col items-center justify-center text-center">
                        <svg
                            className="w-12 h-12 text-alsero-light-gray mb-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                            />
                        </svg>
                        <p className="text-sm text-alsero-dark-gray font-medium mb-1">
                            {getVariable('filters_no_filters', 'No filters available')}
                        </p>
                        <p className="text-xs text-alsero-light-gray">
                            {getVariable('filters_not_configured', 'Filter configuration is not available for this product type.')}
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className={`space-y-0 ${className}`}
        >
            {/* Filtre Temizleme Butonu */}
            {hasActiveSidebarFilters && clearSidebarFilters && (
                <div className="mb-4">
                    <button type="button"
                        onClick={clearSidebarFilters}
                        className="w-full px-4 py-2.5 text-sm font-medium text-alsero-first bg-white border-2 border-alsero-first rounded-lg hover:bg-alsero-first hover:text-white transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M12 4L4 12M4 4l8 8"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        {getVariable('filters_clear_all', 'Clear All Filters')}
                    </button>
                </div>
            )}
            
            {Object.entries(groupedFilters)
                .filter(([groupKey, groupFilters]) => {
                    // Location grubunu gizle (isteğe bağlı)
                    if (hideLocationGroup) {
                        const keyLower = groupKey.toLowerCase()
                        if (keyLower === 'location' || keyLower.includes('filter by location')) return false
                    }
                    // Boş grupları filtrele
                    if (groupFilters.length === 0) return false
                    
                    // Render edilebilir filtreleri kontrol et
                    const renderableFilters = groupFilters.map((filter) => {
                        // Geçersiz filtreleri kontrol et
                        if (filter.field_type === 'range' && (filter.min === undefined || filter.max === undefined)) {
                            return null
                        }
                        if ((filter.field_type === 'checkbox' || filter.field_type === 'dropdown') && (!filter.options || filter.options.length === 0)) {
                            return null
                        }
                        return filter
                    }).filter(Boolean)
                    
                    return renderableFilters.length > 0
                })
                .map(([groupKey, groupFilters]) => {
                    // "Filter By" kısmını kaldır, sadece başlığı göster
                    let groupTitle = groupKey
                    if (groupTitle.startsWith('Filter By ')) {
                        groupTitle = groupTitle.replace('Filter By ', '')
                    }
                    
                    // Group title çevirileri
                    const translatedTitle = groupTitle === 'Other Filters' || groupTitle === 'other'
                        ? getVariable('filters_other', 'Other Filters')
                        : groupTitle === 'Price' || groupTitle === 'Filter By Price'
                        ? getVariable('filters_price', 'Price')
                        : groupTitle === 'Duration' || groupTitle === 'Filter By Duration'
                        ? getVariable('filters_duration', 'Duration')
                        : groupTitle === 'Location' || groupTitle === 'Filter By Location'
                        ? getVariable('filters_location', 'Location')
                        : groupTitle === 'Transfer' || groupTitle === 'Filter By Transfer'
                        ? getVariable('filters_transfer', 'Transfer')
                        : groupTitle === 'Season' || groupTitle === 'Filter By Season'
                        ? getVariable('filters_season', 'Season')
                        : groupTitle === 'Group Size' || groupTitle === 'Filter By Group Size'
                        ? getVariable('filters_group_size', 'Group Size')
                        : groupTitle === 'Tour Type' || groupTitle === 'Filter By Tour Type'
                        ? getVariable('filters_tour_type', 'Tour Type')
                        : groupTitle === 'Rental Type' || groupTitle === 'Filter By Rental Type'
                        ? getVariable('filters_rental_type', 'Rental Type')
                        : groupTitle === 'Capacity' || groupTitle === 'Filter By Capacity'
                        ? getVariable('filters_capacity', 'Capacity')
                        : groupTitle === 'Specifications' || groupTitle === 'Filter By Specifications'
                        ? getVariable('filters_specifications', 'Specifications')
                        : groupTitle === 'Equipment' || groupTitle === 'Filter By Equipment'
                        ? getVariable('filters_equipment', 'Equipment')
                        : groupTitle === 'Category'
                        ? getVariable('filters_category', 'Category')
                        : groupTitle === 'Yacht Type' || groupTitle === 'Filter By Yacht Type'
                        ? getVariable('filters_yacht_type', 'Yacht Type')
                        : groupTitle

                    // Render edilebilir filtreleri filtrele (null dönenleri çıkar)
                    const renderableFilters = groupFilters.map((filter) => renderFilter(filter)).filter(Boolean)
                    
                    // Eğer render edilebilir filtre yoksa bu grubu gösterme
                    if (renderableFilters.length === 0) {
                        return null
                    }

                    return (
                        <FilterGroup key={groupKey} title={translatedTitle} defaultOpen={true}>
                            <div className="space-y-6">
                                {renderableFilters}
                            </div>
                        </FilterGroup>
                    )
                })}
        </motion.aside>
    )
}
